/* eslint no-eval: 0 */
import React from 'react';
import styled, { css, keyframes } from 'styled-components';
import theme from './theme';

export const Gradient = (g1, g2) =>
  css`radial-gradient(ellipse farthest-corner at top left, ${g1} 0%, ${g2} 100%)`;

export const Truncate = width => css`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  min-width: 0;
`;

export const hexa = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  if (alpha >= 0) {
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return `rgb(${r}, ${g}, ${b})`;
};

export const SimpleSpinner = ({ size }: { size: string }) => (
  <StyledSpinner viewBox="0 0 40 40" size={size}>
    <circle
      className="path"
      cx="20"
      cy="20"
      r="15"
      fill="none"
      strokeWidth="4"
    />
  </StyledSpinner>
);

const StyledSpinner = styled.svg`
  animation: rotate 2s linear infinite;
  margin: -25px 0 0 -25px;
  width: ${({ size }) => size || '30px'};
  height: ${({ size }) => size || '30px'};

  & .path {
    stroke: #5652bf;
    stroke-linecap: round;
    animation: dash 1.5s ease-in-out infinite;
  }

  @keyframes rotate {
    100% {
      transform: rotate(360deg);
    }
  }
  @keyframes dash {
    0% {
      stroke-dasharray: 1, 150;
      stroke-dashoffset: 0;
    }
    50% {
      stroke-dasharray: 90, 150;
      stroke-dashoffset: -35;
    }
    100% {
      stroke-dasharray: 90, 150;
      stroke-dashoffset: -124;
    }
  }
`;

export const Shadow = {
  low: '0 2px 8px',
  mid: '0 4px 12px',
  high: '0 8px 16px'
};

export const Transition = {
  hover: {
    on: 'all 0.2s ease-in',
    off: 'all 0.2s ease-out'
  },
  reaction: {
    on: 'all 0.15s ease-in',
    off: 'all 0.1s ease-out'
  },
  dropdown: {
    off: 'all 0.35s ease-out'
  }
};

export const zIndex = new function() {
  // Write down a camel-cased element descriptor as the name (e.g. modal or chatInput).
  // Define at a component level here, then use math to handle order at a local level.
  // (e.g. const ModalInput = styled.input`z-index: zIndex.modal + 1`;)
  // This uses constructor syntax because that allows self-referential math

  this.base = 1; // z-index: auto content will go here or inherit z-index from a parent

  this.background = this.base - 1; // content that should always be behind other things (e.g. textures/illos)
  this.hidden = this.base - 2; // this content should be hidden completely (USE ADD'L MEANS OF HIDING)

  this.card = this.base + 1; // all cards should default to one layer above the base content
  this.loading = this.card + 1; // loading elements should never appear behind cards
  this.avatar = this.card + 1; // avatars should never appear behind cards
  this.form = this.card + 1; // form elements should never appear behind cards
  this.search = this.form; // search is a type of form and should appear at the same level
  this.dmInput = this.form;

  this.composerToolbar = 2000; // composer toolbar - should sit in between most elements

  this.chrome = 3000; // chrome should be visible in modal contexts
  this.navBar = this.chrome; // navBar is chrome and should appear at the same level
  this.mobileInput = this.chrome + 1; // the chatInput on mobile should appear above the navBar
  this.dropDown = this.chrome + 1; // dropDowns shouldn't appear behind the navBar

  this.slider = window.innerWidth < 768 ? this.chrome + 1 : this.chrome; // slider should appear significantly above the base to leave room for other elements
  this.composer = this.slider - 2; // composer should never appear above the slider
  this.chatInput = this.slider + 1; // the slider chatInput should always appear above the slider
  this.flyout = this.chatInput + 3; // flyout may overlap with chatInput and should take precedence

  this.fullscreen = 4000; // fullscreen elements should cover all screen content except toasts

  this.modal = 5000; // modals should completely cover base content and slider as well
  this.gallery = this.modal + 1; // gallery should never appear behind a modal

  this.toast = 6000; // toasts should be visible in every context
  this.tooltip = this.toast + 1; // tooltips should always be on top
}();

export const fontStack = css`
  font-family: -apple-system, BlinkMacSystemFont, 'Helvetica', 'Segoe',
    sans-serif;
`;

export const monoStack = css`
  font-family: 'Input Mono', 'Menlo', 'Inconsolata', 'Roboto Mono', monospace;
`;

const rotate360 = keyframes`
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
`;

export const Spinner = styled.div`
  color: #333;
  font-size: 14px;
  margin-top: 200px;
  text-align: center;
  &:before {
    display: inline-block;
    content: '';
    width: 35px;
    height: 35px;
    border-radius: 50%;
    border: solid 3px transparent;
    border-bottom-color: var(--bg-dark);
    border-right-color: var(--bg-dark);
    animation: ${rotate360} 0.45s linear 0s infinite normal none running;
    margin-right: 6px;
    vertical-align: bottom;
  }
`;

export const LogoLoader = styled.div`
  color: #333;
  font-size: 14px;
  margin-top: 200px;
  text-align: center;
  img {
    margin: 0 auto;
    width: 50px;
  }
`;

export const Label = styled.label`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 0.5rem;
  font-weight: 500;
  font-size: 0.875rem;
  letter-spacing: -0.4px;
  color: ${theme.text.default};

  &:not(:first-of-type) {
    margin-top: 1.5rem;
  }

  a {
    text-decoration: underline;
  }
`;

export const PrefixLabel = styled.label`
  display: flex;
  width: 100%;
  font-size: 12px;
  font-weight: 500;
  color: ${theme.text.placeholder};

  > input {
    margin-left: 2px;
  }
`;

export const Input = styled.input`
  width: 100%;
  color: ${theme.colors.text.primary};
  height: ${props => (props.type === 'textarea' ? '100px' : '38px')};
  box-sizing: border-box;
  border-radius: 2px;
  transition: all 200ms linear;
  font-size: ${({
    theme: {
      typography: { input }
    }
  }) => input.text.fontSize};
  font-weight: ${({
    theme: {
      typography: { input }
    }
  }) => input.text.fontWeight};
  line-height: ${({
    theme: {
      typography: { input }
    }
  }) => input.text.lineHeight};
  border: 1px solid ${theme.colors.border.primary};
  padding: 10px;
  margin-bottom: 2px;
  -webkit-appearance: none;

  &:hover {
    // border-color: ${theme.colors.border.hover};
  }

  &:focus {
    outline: none;
    border-color: ${theme.colors.border.active};
  }

  &[disabled] {
    pointer-events: none;
    color: ${theme.colors.text.disabled};
    border-color: ${theme.colors.border.disabled};
    background-color: ${theme.colors.input.disabled};
    font-weight: ${theme.typography.input.disabled.fontWeight};
  }

  &[data-has-error] {
    border-color: red;
    border-color: ${theme.colors.border.error};
    background-color: ${theme.colors.background.error};
  }

  &[type='password'] {
    letter-spacing: 5px;
    font-size: 20px;
  }
`;

export const InlineInput = styled(Input)`
  padding: 5px 0;
  color: #535f6c;
  &:hover {
    // border-color: #efefef;
  }
`;

export const TextArea = styled.textarea`
  flex: 1 0 auto;
  width: 100%;
  background: ${theme.bg.default};
  font-weight: 500;
  font-size: 0.875rem;
  border: 1px solid ${theme.colors.border.primary};
  border-radius: 0.25rem;
  padding: 0.75rem;
  margin-top: 0.125rem;
  box-shadow: none;

  &::placeholder {
    color: ${theme.text.placeholder};
  }
  &::-webkit-input-placeholder {
    color: ${theme.text.placeholder};
  }
  &:-moz-placeholder {
    color: ${theme.text.placeholder};
  }
  &:-ms-input-placeholder {
    color: ${theme.text.placeholder};
  }

  &:focus {
    border-color: ${theme.brand.default};
    outline: none;
  }
`;
export const InlineTextArea = styled(TextArea)`
  // border-color: #fff;
  outline: none;
  height: 80px;
  color: #535f6c;
  padding: 5px;
  background: #fff;

  &:hover {
    // border-color: #efefef;
  }
`;

export const UnderlineInput = styled.input`
  font-size: inherit;
  font-weight: inherit;
  color: ${theme.text.default};
  border-bottom: 0.125rem solid ${theme.bg.inactive};

  &:focus {
    border-color: ${theme.brand.default};
  }
`;

export const H1 = styled.h1`
  ${fontStack};
  color: ${theme.text.default};
  font-weight: 600;
  font-size: 18px;
  line-height: 1.25;
  margin: 0;
  padding: 0;
  letter-spacing: -0.25px;
`;

export const H2 = styled.h2`
  color: ${theme.text.default};
  ${fontStack};
  font-weight: 700;
  font-size: 1.25rem;
  line-height: 1.25;
  margin: 0;
  padding: 0;
`;

export const H3 = styled.h3`
  color: ${theme.text.default};
  ${fontStack};
  font-weight: 500;
  font-size: 1rem;
  line-height: 1.5;
  margin: 0;
  padding: 0;
`;

export const H4 = styled.h4`
  color: ${theme.text.default};
  ${fontStack};
  font-weight: 500;
  font-size: 0.875rem;
  line-height: 1.4;
  margin: 0;
  padding: 0;
`;
export const SectionTitle = styled.h5`
  font-size: 14px;
  font-weight: 600;
  color: #435268;
`;
export const H5 = styled.h5`
  color: ${theme.text.default};
  ${fontStack};
  font-weight: 500;
  font-size: 0.75rem;
  line-height: 1.4;
  margin: 0;
  padding: 0;
`;

export const H6 = styled.h6`
  color: ${theme.text.default};
  ${fontStack};
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.675rem;
  line-height: 1.5;
  margin: 0;
  padding: 0;
`;

export const P = styled.p`
  color: ${theme.text.default};
  ${fontStack};
  font-weight: 400;
  font-size: 0.875rem;
  line-height: 1.4;
  margin: 0;
  padding: 0;
`;

export const Span = styled.span`
  color: ${theme.text.default};
  ${fontStack};
  font-weight: 400;
  font-size: 0.875rem;
  line-height: 1.4;
  margin: 0;
  padding: 0;
`;

export const FlexRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
`;

export const SecondaryButton = styled.button`
  display: inline-block;
  padding: 9px 16px;
  min-width: 96px;
  border-radius: 3px;
  background: #e0e5ff;
  color: ${theme.colors.faded};
  font-size: ${theme.size.default};
  font-weight: ${theme.weight.default};
  cursor: pointer;
`;

export const PrimaryButton = styled.button`
  display: inline-block;
  padding: 9px 16px;
  min-width: 96px;
  border-radius: 3px;
  background: ${theme.colors.brand};
  color: ${theme.colors.white};
  font-size: ${theme.size.default};
  font-weight: ${theme.weight.default};
  cursor: pointer;
`;

export const FlexCol = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
`;

const returnTooltip = props => {
  switch (props.tipLocation) {
    case 'top-left':
      return `
          &:after {
            bottom: calc(100% + 4px);
            right: 0;
          }
          &:before {
            bottom: 100%;
            right: 0;
            transform: translateX(-100%);
            border-bottom-width: 0;
            border-top-color: ${
              props.onboarding ? theme.brand.alt : theme.bg.dark
            };
          }
      `;
    case 'top-right':
      return `
          &:after {
            bottom: calc(100% + 4px);
            left: 0;
          }
          &:before {
            bottom: 100%;
            left: 0;
            transform: translateX(100%);
            border-bottom-width: 0;
            border-top-color: ${
              props.onboarding ? theme.brand.alt : theme.bg.dark
            };
          }
      `;
    case 'top':
      return `
          &:after {
            bottom: calc(100% + 8px);
            left: 50%;
            transform: translateX(-50%);
          }
          &:before {
            bottom: calc(100% + 3px);
            left: 50%;
            transform: translateX(-50%);
            border-bottom-width: 0;
            border-top-color: ${
              props.onboarding ? theme.brand.alt : theme.bg.dark
            };
          }
      `;
    case 'right':
    default:
      return `
          &:after {
            top: 50%;
            left: calc(100% + 9px);
            transform: translateY(-50%);
          }
          &:before{
            top: calc(50% - 5px);
            left: calc(100% + 4px);
            border-left-width: 0;
            border-right-color: ${
              props.onboarding ? theme.brand.alt : theme.bg.dark
            };
          }
      `;
    case 'bottom-left':
      return `
          &:after {
            top: calc(100% + 4px);
            right: 0;
          }
          &:before {
            top: 100%;
            right: 0;
            transform: translateX(-100%);
            border-top-width: 0;
            border-bottom-color: ${
              props.onboarding ? theme.brand.alt : theme.bg.dark
            };
          }
      `;
    case 'bottom-right':
      return `
          &:after {
            top: calc(100% + 4px);
            left: 0;
          }
          &:before {
            top: 100%;
            left: 0;
            transform: translateX(100%);
            border-top-width: 0;
            border-bottom-color: ${
              props.onboarding ? theme.brand.alt : theme.bg.dark
            };
          }
      `;
    case 'bottom':
      return `
        &:after {
          top: calc(100% + 8px);
          left: 50%;
          transform: translateX(-50%);
        }
        &:before {
          top: calc(100% + 3px);
          left: 50%;
          transform: translateX(-50%);
          border-top-width: 0;
          border-bottom-color: ${
            props.onboarding ? theme.brand.alt : theme.bg.dark
          };
        }
      `;
    case 'left':
      return `
          &:after {
            right: calc(100% + 4px);
            top: 50%;
            transform: translateY(-50%);
          }
          &:before{
            right: 100%;
            top: calc(50% - 5px);
            border-right-width: 0;
            border-left-color: ${
              props.onboarding ? theme.brand.alt : theme.bg.dark
            };
          }
      `;
  }
};

export const Tooltip = props => css`
  position: relative;

  &:after,
  &:before {
    line-height: 1;
    user-select: none;
    pointer-events: none;
    position: absolute;
    opacity: 0;
    display: block;
    text-transform: none;
  }

  &:before {
    content: '';
    z-index: ${zIndex.tooltip + 1};
    border: 6px solid transparent;
  }

  &:after {
    content: ${
      props.tipText && !props.onboarding
        ? `'${CSS.escape(props.tipText)}'`
        : "''"
    };
    z-index: ${zIndex.tooltip};
    ${fontStack};
    font-size: 14px;
    font-weight: 500;
    min-width: 8px;
    max-width: 21em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding: 8px 12px;
    border-radius: 3px;
    /* box-shadow: ${Shadow.mid} ${hexa(theme.bg.reverse, 0.25)}; */
    background: ${theme.bg.dark};
    color: ${theme.text.white};
  }

  ${props.tipText && !props.onboarding ? returnTooltip(props) : ''};

  &:hover:after,
  &:hover:before {
    opacity: 1;
    transition: opacity 0.1s ease-in 0.1s;
  }

  @media (max-width: 768px) {
    &:after,
    &:before {
      display: none;
    }
  }
`;

export const Onboarding = props => css`
  position: relative;

  &:after,
  &:before {
    line-height: 1;
    user-select: none;
    pointer-events: none;
    position: absolute;
    opacity: 0;
    display: block;
  }

  &:before {
    content: '';
    z-index: ${zIndex.tooltip + 1};
    border: 5px solid transparent;
  }

  &:after {
    content: ${props.onboarding ? `'${props.onboarding}'` : "''"};
    z-index: ${zIndex.tooltip};
    ${fontStack};
    text-align: left;
    line-height: 20px;
    font-size: 14px;
    font-weight: 500;
    width: 300px;
    white-space: normal;
    overflow: hidden;
    padding: 16px;
    padding-left: 20px;
    border-radius: 12px;
    background-color: ${theme.bg.default};
    background: ${theme.bg.default} url(/img/goopy-top.svg) center top no-repeat;
    background-size: 100%;
    color: ${theme.text.default};
    box-shadow: 0 8px 32px rgba(23, 26, 33, 0.35);
  }

  ${props.onboarding ? returnTooltip(props) : ''};

  &:after,
  &:before {
    opacity: 1;
    transition: opacity 0.1s ease-in 0.1s;
  }
`;

export const HorizontalRule = styled(FlexRow)`
  position: relative;
  justify-content: center;
  align-items: center;
  align-self: stretch;
  color: ${theme.bg.border};

  hr {
    display: inline-block;
    flex: 1 0 auto;
    border-top: 1px solid ${theme.bg.border};
  }

  div {
    margin: 0 16px;
  }
`;

export const SectionHeader = styled.div`
  height: 59px;
  background-color: #ffffff;
  display: flex;
  align-items: center;
  padding: 0 20px;
`;

export const InfoBox = styled.div`
  background: #f7f7f7;
  border: 1px solid #eeeeee;
  color: #555;
  width: auto;
  cursor: default;
  line-height: 18px;
  text-align: center;
  position: relative;
  padding: 9px 14px;
  text-transform: uppercase;
  font-size: 12px;
  font-weight: 600;
  border-radius: 3px;
  height: 36px;
  user-select: none;
`;
